import React, { ReactNode, useState } from "react";
import { Alert } from "react-bootstrap";

import MetricsTable, { ColumnGroup, SortConfig } from "./MetricsTable";
import { formatHumanDuration } from "../../utils/analytics";

type HeatmapMetricsTableProps = {
    data: Record<string, any>[];
    emptyMessage: string;
    initialSortKey?: string;
    preferredLeadingKeys?: string[];
};

const HeatmapMetricsTable: React.FC<HeatmapMetricsTableProps> = ({
    data,
    emptyMessage,
    initialSortKey,
    preferredLeadingKeys = []
}) => {
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: initialSortKey || "",
        direction: "desc"
    });

    const toTitleCase = (value: string): string => value
        .replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase());

    const toNumber = (value: number | string | null | undefined): number | null => {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const isPercentKey = (columnKey: string): boolean => /(_pct$|percent|percentage)/i.test(columnKey);
    const isPopPctKey = (columnKey: string): boolean => /_pop_pct$/i.test(columnKey);
    const isDurationKey = (columnKey: string): boolean => /hours?_watched|avg_hours_watched|watch[_ ]?time|watch_time|total_watch_time_in_seconds/i.test(columnKey);
    const getDurationUnit = (columnKey: string): "hours" | "seconds" => {
        if (/hours?_watched|avg_hours_watched/i.test(columnKey)) {
            return "hours";
        }

        return "seconds";
    };

    const formatTableValue = (value: any, columnKey?: string): ReactNode => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }

        if (columnKey && isDurationKey(columnKey)) {
            const parsed = toNumber(value);
            if (parsed === null) {
                return "-";
            }

            return formatHumanDuration(parsed, getDurationUnit(columnKey));
        }

        if (typeof value === "number") {
            if (!Number.isFinite(value)) {
                return "-";
            }

            if (columnKey && isPercentKey(columnKey)) {
                return `${value.toFixed(2)}%`;
            }

            if (Number.isInteger(value)) {
                return value.toLocaleString();
            }

            return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
        }

        if (typeof value === "boolean") {
            return value ? "Yes" : "No";
        }

        return String(value);
    };

    const getHeatmapCell = (value: number | string | null | undefined, maxValue: number, columnKey: string): ReactNode => {
        const parsed = toNumber(value);
        const normalized = parsed === null || maxValue <= 0 ? 0 : Math.max(0, Math.min(1, parsed / maxValue));
        const alpha = 0.12 + (0.58 * normalized);

        return (
            <div
                style={{
                    background: `rgba(255, 72, 140, ${alpha})`,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontWeight: 600
                }}
            >
                {formatTableValue(value, columnKey)}
            </div>
        );
    };

    const getPopHeatCell = (value: number | string | null | undefined, maxAbsValue: number, columnKey: string): ReactNode => {
        const parsed = toNumber(value);
        if (parsed === null) {
            return "-";
        }

        const normalized = maxAbsValue <= 0 ? 0 : Math.max(0, Math.min(1, Math.abs(parsed) / maxAbsValue));
        const alpha = 0.12 + (0.48 * normalized);
        const background = parsed > 0
            ? `rgba(40, 167, 69, ${alpha})`
            : parsed < 0
                ? `rgba(220, 53, 69, ${alpha})`
                : "rgba(108, 117, 125, 0.2)";

        return (
            <div
                style={{
                    background,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontWeight: 600
                }}
            >
                {formatTableValue(parsed, columnKey)}
            </div>
        );
    };

    const getColumns = (rows: Record<string, any>[]): string[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const keys: string[] = [];
        rows.forEach(row => {
            Object.keys(row).forEach(key => {
                if (key === "rowKey") {
                    return;
                }

                if (!keys.includes(key)) {
                    keys.push(key);
                }
            });
        });

        if (keys.length === 0) {
            return [];
        }

        const preferred = [...preferredLeadingKeys, "brand_name", "brand", "thing_brandname", "genre", "genre_name", "name"];
        let leadingKey = preferred.find(key => keys.includes(key));

        if (!leadingKey) {
            leadingKey = keys.find(key => /(brand|genre)/i.test(key));
        }

        if (!leadingKey) {
            leadingKey = keys.find(key => rows.some(row => typeof row[key] === "string"));
        }

        if (!leadingKey) {
            return keys;
        }

        return [leadingKey, ...keys.filter(key => key !== leadingKey)];
    };

    const getSortedData = (): Array<Record<string, any> & { rowKey: string }> => {
        const rows: Array<Record<string, any> & { rowKey: string }> = data.map((row, index) => ({
            ...row,
            rowKey: `${String(row.thing_brandname ?? row.brand_name ?? row.genre ?? row.genre_name ?? row.name ?? `row-${index}`)}-${index}`
        }));

        const sortKey = sortConfig.key || initialSortKey;
        if (!sortKey) {
            return rows;
        }

        const multiplier = sortConfig.direction === "asc" ? 1 : -1;

        return rows.sort((a, b) => {
            const aNum = toNumber(a[sortKey] as number | string | null | undefined);
            const bNum = toNumber(b[sortKey] as number | string | null | undefined);

            if (aNum !== null && bNum !== null) {
                return (aNum - bNum) * multiplier;
            }

            const aString = String(a[sortKey] ?? "").toLowerCase();
            const bString = String(b[sortKey] ?? "").toLowerCase();
            return aString.localeCompare(bString) * multiplier;
        });
    };

    const tableData = getSortedData();

    const getColumnGroups = (): ColumnGroup[] => {
        const columns = getColumns(tableData);
        if (columns.length === 0) {
            return [];
        }

        const leadingKey = columns[0];
        const metricColumns = columns.slice(1);
        const maxByColumn: Record<string, number> = {};
        const maxAbsByColumn: Record<string, number> = {};

        metricColumns.forEach(column => {
            const values = tableData.map(row => toNumber(row[column])).filter((v): v is number => v !== null);
            maxByColumn[column] = values.length > 0 ? Math.max(...values) : 0;
            maxAbsByColumn[column] = values.length > 0 ? Math.max(...values.map(v => Math.abs(v))) : 0;
        });

        return [{
            name: "Metrics",
            columns: columns.map(column => {
                if (column === leadingKey) {
                    return {
                        key: column,
                        header: toTitleCase(column),
                        sortable: true,
                        render: (value: any) => formatTableValue(value, column)
                    };
                }

                return {
                    key: column,
                    header: toTitleCase(column),
                    sortable: true,
                    render: (value: any) => {
                        if (isPopPctKey(column)) {
                            return getPopHeatCell(value, maxAbsByColumn[column], column);
                        }

                        const numeric = toNumber(value);
                        if (numeric !== null) {
                            return getHeatmapCell(value, maxByColumn[column], column);
                        }

                        return formatTableValue(value, column);
                    }
                };
            })
        }];
    };

    const columnGroups = getColumnGroups();

    const onSortChange = (key: string): void => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }

            return { key, direction: key.includes("name") ? "asc" : "desc" };
        });
    };

    if (tableData.length === 0) {
        return <Alert variant="info" style={{ marginBottom: 0 }}>{emptyMessage}</Alert>;
    }

    return (
        <MetricsTable
            data={tableData}
            columnGroups={columnGroups}
            rowKeyField="rowKey"
            emptyMessage={emptyMessage}
            sortConfig={sortConfig}
            onSortChange={onSortChange}
        />
    );
};

export default HeatmapMetricsTable;
